import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";

import { LineReader } from "./lineReader";

export const LineReaderContext = createContext<{
  enabled: boolean;

  // This allows us to ensure only a single line reader is active at a time
  activeLineReader: LineReader | null;
  setActiveLineReader: (lineReader: LineReader | null) => void;
} | null>(null);

export const useLineReader = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const readerRef = useRef<LineReader | null>(null);

  const { enabled, activeLineReader, setActiveLineReader } = useContext(
    LineReaderContext,
  ) || { enabled: false };

  // mounting the line reader lets it start listening for events,
  // but does not immediately start masking the text
  useEffect(() => {
    if (containerRef.current && enabled) {
      readerRef.current = new LineReader(containerRef.current);
      readerRef.current.mount();
    }

    return () => {
      readerRef.current?.unmount();
      readerRef.current = null;
    };
  }, [enabled]);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (activeLineReader !== readerRef.current) {
        // if the line reader is already active, we need to unmount it
        activeLineReader?.unmount();
        setActiveLineReader?.(readerRef.current);
      }
      readerRef.current?.handleClick(e);
    },
    [activeLineReader, setActiveLineReader],
  );

  return {
    containerRef,
    handleClick,
  };
};
